/**
 * ActionPreview - Shows a preview of an AI action with confirmation UI
 *
 * Displays:
 * - Action type and parameters
 * - Success/error states
 * - Confirmation buttons for pending actions
 * - Links to created/updated entities
 * - Undo option for executed actions
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConversationAction } from '@services/api/chat';

interface ActionPreviewProps {
  action: ConversationAction;
  isPending: boolean;
  onConfirm: (confirmed: boolean, modifications?: Record<string, unknown> | undefined) => void;
  onUndo: () => void;
  onTaskClick?: ((taskId: string, boardId: string) => void) | undefined;
  onBoardClick?: ((boardId: string) => void) | undefined;
  onProjectClick?: ((projectId: string) => void) | undefined;
  onMeetingClick?: ((meetingId: string) => void) | undefined;
  compact?: boolean | undefined;
}

export default function ActionPreview({
  action,
  isPending,
  onConfirm,
  onUndo,
  onTaskClick,
  onBoardClick,
  onProjectClick,
  onMeetingClick,
  compact = false,
}: ActionPreviewProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(isPending);

  const getActionIcon = () => {
    if (action.status === 'executed') {
      return (
        <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    }
    if (action.status === 'failed') {
      return (
        <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      );
    }
    if (action.status === 'pending') {
      return (
        <svg className="w-4 h-4 text-yellow-600 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    return null;
  };

  const getActionLabel = () => {
    const labels: Record<string, string> = {
      create_task: 'Create Task',
      update_task: 'Update Task',
      delete_task: 'Delete Task',
      create_board: 'Create Board',
      update_board: 'Update Board',
      list_boards: 'List Boards',
      create_project: 'Create Project',
      list_projects: 'List Projects',
      get_project_summary: 'Project Summary',
      create_meeting: 'Create Meeting',
      list_meetings: 'List Meetings',
      search_meeting_notes: 'Search Meeting Notes',
      get_meeting_summary: 'Get Meeting Summary',
      search_emails: 'Search Emails',
      get_email_details: 'Get Email Details',
      search_tasks: 'Search Tasks',
      list_tasks: 'List Tasks',
      unified_search: 'Search',
      search_knowledge: 'Knowledge Search',
      query_documents: 'Query Documents',
      get_board_stats: 'Get Board Stats',
      bulk_update_tasks: 'Bulk Update Tasks',
    };
    return labels[action.action_type] || action.action_type;
  };

  const getBorderColor = () => {
    if (action.status === 'executed') return 'border-green-200 dark:border-green-800';
    if (action.status === 'failed') return 'border-red-200 dark:border-red-800';
    if (action.status === 'pending') return 'border-yellow-200 dark:border-yellow-800';
    return 'border-neutral-200 dark:border-neutral-700';
  };

  const getBgColor = () => {
    if (action.status === 'executed') return 'bg-green-50 dark:bg-green-900/20';
    if (action.status === 'failed') return 'bg-red-50 dark:bg-red-900/20';
    if (action.status === 'pending') return 'bg-yellow-50 dark:bg-yellow-900/20';
    return 'bg-neutral-50 dark:bg-neutral-800/50';
  };

  const renderParameters = () => {
    const params = action.parameters;
    const importantKeys = ['title', 'name', 'description', 'priority', 'status', 'due_date'];
    const displayParams = Object.entries(params)
      .filter(([key]) => importantKeys.includes(key))
      .slice(0, 3);

    if (displayParams.length === 0) return null;

    return (
      <div className="mt-2 space-y-1">
        {displayParams.map(([key, value]) => (
          <div key={key} className={`flex items-start gap-2 ${compact ? 'text-xs' : 'text-sm'}`}>
            <span className="text-neutral-500 dark:text-neutral-400 capitalize min-w-[80px]">
              {key.replace(/_/g, ' ')}:
            </span>
            <span className="text-neutral-900 dark:text-white font-medium flex-1">
              {String(value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderListResults = (result: any) => {
    // Render meetings list
    if (result.meetings && Array.isArray(result.meetings) && result.meetings.length > 0) {
      return (
        <div className="space-y-1.5 max-h-60 overflow-y-auto">
          {result.meetings.map((meeting: any, idx: number) => (
            <div
              key={meeting.id || idx}
              className="p-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded text-xs hover:border-primary-400 dark:hover:border-primary-600 transition-colors cursor-pointer"
              onClick={() => meeting.id && onMeetingClick?.(meeting.id)}
            >
              <div className="font-medium text-neutral-900 dark:text-white">
                {meeting.title || t('meetings.untitledMeeting')}
              </div>
              {meeting.start_time && (
                <div className="text-neutral-500 dark:text-neutral-400 mt-0.5">
                  📅 {new Date(meeting.start_time).toLocaleString()}
                </div>
              )}
              {meeting.description && (
                <div className="text-neutral-600 dark:text-neutral-300 mt-1 line-clamp-2">
                  {meeting.description}
                </div>
              )}
              {meeting.status && (
                <div className="mt-1">
                  <span className="inline-block px-1.5 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded text-xs">
                    {meeting.status}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    // Render emails list
    if (result.emails && Array.isArray(result.emails) && result.emails.length > 0) {
      return (
        <div className="space-y-1.5 max-h-60 overflow-y-auto">
          {result.emails.map((email: any, idx: number) => (
            <div
              key={email.id || idx}
              className="p-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded text-xs hover:border-primary-400 dark:hover:border-primary-600 transition-colors"
            >
              <div className="font-medium text-neutral-900 dark:text-white">
                {email.subject || 'No Subject'}
              </div>
              {email.sender && (
                <div className="text-neutral-500 dark:text-neutral-400 mt-0.5">
                  From: {email.sender}
                </div>
              )}
              {email.received_at && (
                <div className="text-neutral-500 dark:text-neutral-400 mt-0.5">
                  📅 {new Date(email.received_at).toLocaleString()}
                </div>
              )}
              {email.snippet && (
                <div className="text-neutral-600 dark:text-neutral-300 mt-1 line-clamp-2">
                  {email.snippet}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    // Render tasks list
    if (result.tasks && Array.isArray(result.tasks) && result.tasks.length > 0) {
      return (
        <div className="space-y-1.5 max-h-60 overflow-y-auto">
          {result.tasks.map((task: any, idx: number) => (
            <div
              key={task.id || idx}
              className="p-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded text-xs hover:border-primary-400 dark:hover:border-primary-600 transition-colors cursor-pointer"
              onClick={() => task.id && task.board_id && onTaskClick?.(task.id, task.board_id)}
            >
              <div className="font-medium text-neutral-900 dark:text-white">
                {task.title || t('task.untitledTask')}
              </div>
              {task.description && (
                <div className="text-neutral-600 dark:text-neutral-300 mt-1 line-clamp-2">
                  {task.description}
                </div>
              )}
              <div className="flex items-center gap-2 mt-1">
                {task.status && (
                  <span className="inline-block px-1.5 py-0.5 bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 rounded text-xs">
                    {task.status}
                  </span>
                )}
                {task.priority && (
                  <span className={`inline-block px-1.5 py-0.5 rounded text-xs ${
                    task.priority === 'HIGH' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' :
                    task.priority === 'MEDIUM' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' :
                    'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                  }`}>
                    {task.priority}
                  </span>
                )}
                {task.due_date && (
                  <span className="text-neutral-500 dark:text-neutral-400">
                    Due: {new Date(task.due_date).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Render boards list
    if (result.boards && Array.isArray(result.boards) && result.boards.length > 0) {
      return (
        <div className="space-y-1.5 max-h-60 overflow-y-auto">
          {result.boards.map((board: any, idx: number) => (
            <div
              key={board.id || idx}
              className="p-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded text-xs hover:border-primary-400 dark:hover:border-primary-600 transition-colors cursor-pointer"
              onClick={() => board.id && onBoardClick?.(board.id)}
            >
              <div className="font-medium text-neutral-900 dark:text-white">
                {board.name || t('board.untitledBoard')}
              </div>
              {board.description && (
                <div className="text-neutral-600 dark:text-neutral-300 mt-1 line-clamp-2">
                  {board.description}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    // Render projects list
    if (result.projects && Array.isArray(result.projects) && result.projects.length > 0) {
      return (
        <div className="space-y-1.5 max-h-60 overflow-y-auto">
          {result.projects.map((project: any, idx: number) => (
            <div
              key={project.id || idx}
              className="p-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded text-xs hover:border-primary-400 dark:hover:border-primary-600 transition-colors cursor-pointer"
              onClick={() => project.id && onProjectClick?.(project.id)}
            >
              <div className="font-medium text-neutral-900 dark:text-white">
                {project.name || t('project.untitled')}
              </div>
              {project.description && (
                <div className="text-neutral-600 dark:text-neutral-300 mt-1 line-clamp-2">
                  {project.description}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  const renderResult = () => {
    if (!action.result) return null;

    const result = action.result as any;

    // Show error
    if (result.error) {
      return (
        <div className="mt-2 p-2 bg-red-100 dark:bg-red-900/30 rounded text-red-700 dark:text-red-300 text-xs">
          {result.error}
        </div>
      );
    }

    // Show success message
    if (result.success && result.message) {
      return (
        <div className="mt-2 text-xs text-green-600 dark:text-green-400">
          {result.message}
        </div>
      );
    }

    // Show entity link
    if (result.entity_id && result.entity_type) {
      return (
        <div className="mt-2">
          <EntityLink
            entityType={result.entity_type}
            entityId={result.entity_id}
            data={result}
            onTaskClick={onTaskClick}
            onBoardClick={onBoardClick}
            onProjectClick={onProjectClick}
            onMeetingClick={onMeetingClick}
            compact={compact}
          />
        </div>
      );
    }

    // Show count for search/bulk operations
    if (result.count !== undefined) {
      return (
        <div className="mt-2 space-y-2">
          <div className="text-xs text-neutral-600 dark:text-neutral-400">
            Found {result.count} {result.count === 1 ? 'result' : 'results'}
          </div>
          {renderListResults(result)}
        </div>
      );
    }

    return null;
  };

  return (
    <div className={`border ${getBorderColor()} ${getBgColor()} rounded-lg p-3 ${compact ? 'text-sm' : ''}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-1">
          {getActionIcon()}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-left flex-1 font-medium text-neutral-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            {getActionLabel()}
          </button>
          {!isPending && action.status === 'executed' && (
            <button
              onClick={onUndo}
              className="text-xs text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
              title="Undo"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
            </button>
          )}
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
        >
          <svg
            className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="mt-2">
          {renderParameters()}
          {renderResult()}

          {/* Confirmation buttons for pending actions */}
          {isPending && action.status === 'pending' && (
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => onConfirm(true)}
                className="flex-1 px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
              >
                ✓ Confirm
              </button>
              <button
                onClick={() => onConfirm(false)}
                className="flex-1 px-3 py-1.5 bg-neutral-300 dark:bg-neutral-600 text-neutral-700 dark:text-neutral-200 rounded-md hover:bg-neutral-400 dark:hover:bg-neutral-500 transition-colors text-sm font-medium"
              >
                ✕ Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ===================================================================
// Entity Link Component
// ===================================================================

interface EntityLinkProps {
  entityType: string;
  entityId: string;
  data: any;
  onTaskClick?: ((taskId: string, boardId: string) => void) | undefined;
  onBoardClick?: ((boardId: string) => void) | undefined;
  onProjectClick?: ((projectId: string) => void) | undefined;
  onMeetingClick?: ((meetingId: string) => void) | undefined;
  compact?: boolean | undefined;
}

function EntityLink({
  entityType,
  entityId,
  data,
  onTaskClick,
  onBoardClick,
  onProjectClick,
  onMeetingClick,
  compact = false,
}: EntityLinkProps) {
  const handleClick = () => {
    if (entityType === 'task' && onTaskClick && data.task?.board_id) {
      onTaskClick(entityId, data.task.board_id);
    } else if (entityType === 'board' && onBoardClick) {
      onBoardClick(entityId);
    } else if (entityType === 'project' && onProjectClick) {
      onProjectClick(entityId);
    } else if (entityType === 'meeting' && onMeetingClick) {
      onMeetingClick(entityId);
    }
  };

  const getIcon = () => {
    switch (entityType) {
      case 'task':
        return (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        );
      case 'board':
        return (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
          </svg>
        );
      case 'project':
        return (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
        );
      case 'meeting':
        return (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getLabel = () => {
    if (entityType === 'task' && data.task?.title) {
      return data.task.title;
    }
    if (entityType === 'board' && data.board?.name) {
      return data.board.name;
    }
    if (entityType === 'project' && data.project?.name) {
      return data.project.name;
    }
    return `View ${entityType}`;
  };

  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 ${compact ? 'text-xs' : 'text-sm'} bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-md hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors border border-primary-200 dark:border-primary-800`}
    >
      {getIcon()}
      <span className="truncate max-w-[200px]">{getLabel()}</span>
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
    </button>
  );
}
